import React, { useCallback, useContext, useMemo, useState } from 'react';
import { LorawanActionTypes } from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Switch,
  Tooltip,
} from 'antd';
import rolesConstants from '@utils/rolesConstants';
import CustomTag from '@components/CustomTag';
import { fromBase64, toBase64 } from '@utils/formatters';
import SessionContext from '@providers/Session/SessionContext';
import { InfoCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

type TextType = 'base64' | 'text';

interface DownlinkScheduleDeviceProps {
  devEui: string;
  base64Payload?: string;
  payloadHex?: string;
  port?: number;
  fPort?: number;
  confirmed: boolean;
  flushDownlinkQueue?: boolean;
}
interface DownlinkScheduleNewDeviceProps {
  devEui: string;
  payloadHex: string;
  fPort: number;
  confirmed: boolean;
  flushDownlinkQueue: boolean;
}
interface DownlinkSchedulePayloadProps {
  action: LorawanActionTypes.DOWNLINK_SCHEDULE;
  devices: DownlinkScheduleDeviceProps[] | DownlinkScheduleNewDeviceProps[];
}

interface DownlinkScheduleProps {
  devices: DownlinkScheduleDeviceProps[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const useDownlinkSchedule = ({
  devices,
  isUpload,
  form,
}: DownlinkScheduleProps): DeviceActionProps => {
  const { t } = useTranslation(['lorawan', 'device-actions']);
  const formValues = Form.useWatch([], form);
  const { hasPermission } = useContext(SessionContext);

  const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);

  const [textType, setTextType] = useState<TextType>('base64');

  const handleTextSwitch = useCallback(
    (type: TextType) => {
      setTextType(type);
      let valueFormatted;
      if (type === 'base64') {
        valueFormatted = toBase64(formValues?.base64Payload);
      } else {
        valueFormatted = fromBase64(formValues?.base64Payload);
      }
      form.setFieldValue('base64Payload', valueFormatted);
    },
    [form, formValues?.base64Payload],
  );

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const downlinkScheduleSteps: DeviceActionStepsProps[] = [
      {
        title: (
          <div>
            {t('payload')}
            &nbsp;
            <Radio.Group
              optionType="button"
              size="small"
              value={textType}
              onChange={({ target: { value } }) => handleTextSwitch(value)}
            >
              <Radio value="base64">{t('base64')}</Radio>
              <Radio value="text">{t('text')}</Radio>
            </Radio.Group>
          </div>
        ),
        name: 'base64Payload',
        content: <TextArea autoSize={{ minRows: 4, maxRows: 4 }} />,
      },
      {
        title: t('confirmed'),
        name: 'confirmed',
        initialValue: false,
        valuePropName: 'checked',
        content: (
          <Switch checked={formValues?.confirmed} style={{ marginLeft: 8 }} />
        ),
        isNotRequired: true,
      },
      {
        title: t('port'),
        name: 'port',
        initialValue: 1,
        isNotRequired: true,
        content: <InputNumber min={0} style={{ width: '100%' }} />,
      },
    ];

    if (hasNewLora) {
      downlinkScheduleSteps.push({
        title: (
          <span>
            {t('flushDownlinkQueue')}
            <Tooltip title={t('flushDownlinkQueueHelp')}>
              <Button
                type="link"
                size="small"
                icon={<InfoCircleOutlined />}
                style={{ padding: '0 4px' }}
              />
            </Tooltip>
          </span>
        ),
        name: 'flushDownlinkQueue',
        initialValue: false,
        valuePropName: 'checked',
        content: (
          <Switch
            checked={formValues?.flushDownlinkQueue}
            style={{ marginLeft: 8 }}
          />
        ),
        isNotRequired: true,
      });
    }

    return downlinkScheduleSteps;
  }, [
    formValues?.confirmed,
    formValues?.flushDownlinkQueue,
    handleTextSwitch,
    hasNewLora,
    t,
    textType,
  ]);

  const payloadToSend = useMemo((): DownlinkSchedulePayloadProps => {
    const payloadValue =
      textType === 'text'
        ? toBase64(formValues?.base64Payload)
        : formValues?.base64Payload;

    const mapLegacyDevice = (device: DownlinkScheduleDeviceProps) => ({
      devEui: device.devEui,
      base64Payload: payloadValue,
      port: formValues?.port,
      confirmed: !!formValues?.confirmed,
    });

    const mapNewDevice = (
      device: DownlinkScheduleDeviceProps,
    ): DownlinkScheduleNewDeviceProps => ({
      devEui: device.devEui,
      payloadHex: payloadValue,
      fPort: formValues?.port,
      confirmed: !!formValues?.confirmed,
      flushDownlinkQueue: !!formValues?.flushDownlinkQueue,
    });

    if (isUpload) {
      return {
        action: LorawanActionTypes.DOWNLINK_SCHEDULE,
        devices,
      };
    }

    if (hasNewLora) {
      return {
        action: LorawanActionTypes.DOWNLINK_SCHEDULE,
        devices: devices?.map(mapNewDevice),
      };
    }

    return {
      action: LorawanActionTypes.DOWNLINK_SCHEDULE,
      devices: devices?.map(mapLegacyDevice),
    };
  }, [
    devices,
    formValues?.base64Payload,
    formValues?.confirmed,
    formValues?.flushDownlinkQueue,
    formValues?.port,
    hasNewLora,
    isUpload,
    textType,
  ]);

  const pStyle = {
    display: 'inline',
    margin: 0,
  };

  const uploadCustomInfo = (
    <Alert
      message={
        <>
          <div>
            <strong>{`${t('base64Payload')}: `}</strong>
            <p style={pStyle}>{t('mustBeBase64Encoded')}</p>
          </div>
          <div>
            <strong>{`${t('confirmed')}: `}</strong>
            <p style={pStyle}>{`${t('toEnableEnter')}: `}</p>
            <CustomTag>true</CustomTag>
            <p style={pStyle}>{`${t('toDisable')}: `}</p>
            <CustomTag>false</CustomTag>
          </div>
          <div>
            <strong>{`${t('port')}: `}</strong>
            <p style={pStyle}>{`${t('standard')}: `}</p>
            <CustomTag>1</CustomTag>
          </div>
          {hasNewLora && (
            <div>
              <strong>{`${t('flushDownlinkQueue')}: `}</strong>
              <p style={pStyle}>{`${t('toEnableEnter')}: `}</p>
              <CustomTag>true</CustomTag>
              <p style={pStyle}>{`${t('toDisable')}: `}</p>
              <CustomTag>false</CustomTag>
            </div>
          )}
        </>
      }
    />
  );

  return {
    steps,
    payloadToSend,
    title: t(
      `device-actions:labels.lorawan.${rolesConstants.LORAWAN_DOWNLINK_SCHEDULE}`,
    ),
    uploadCustomInfo,
  };
};

export default useDownlinkSchedule;
